import i18n from 'i18next';
import { logger } from "@/utils/logger";
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend, { HttpBackendOptions } from 'i18next-http-backend';
import { LOCALIZATION_NAMESPACES } from './namespaceConfig';
import { isRtlLocale, BOOTSTRAP_I18N_CONFIG } from '@/config/localeConfig';
import { getRuntimeRtlChecker } from '@/lib/runtimeRtlChecker';
import { DOMAIN_DEFAULTS } from '@/lib/domainDefaults';

// ==============================================
// i18n Debug Mode (Dev-only toggle for showing raw keys)
// ==============================================

/**
 * Check if Keys debug mode is active (shows raw translation keys)
 * This is a dev-only toggle for debugging missing/broken translations
 * 
 * SIMPLIFIED ARCHITECTURE (2 modes):
 * 1. Production: JSON files → i18next fallback chain → defaultValue prop
 * 2. Edit Mode: DB query + inline editing (superadmin only, via LocalizationEditContext)
 * 
 * Keys mode is a debug overlay, not a full translation source mode
 */
export const isKeysMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem('i18n_debug_keys') === 'true';
};

/**
 * Toggle keys debug mode
 */
export const setKeysMode = (enabled: boolean): void => {
  localStorage.setItem('i18n_debug_keys', enabled ? 'true' : 'false');
};

// Check if error logging is enabled
const isErrorLogEnabled = () => localStorage.getItem('localization_error_log_enabled') === 'true';

// Pending issues for batched logging to database
interface DetectedIssue {
  namespace: string;
  key: string;
  type: 'missing' | 'load_failed' | 'object_returned' | 'parent_child_conflict';
  route: string;
  language: string;
}

// Issue collection for dev console only - no auto-trigger to edge functions
// Issues are logged to console when i18n_debug is enabled
// Database logging is handled by scheduled GitHub Action
const pendingIssues: DetectedIssue[] = [];

// Queue an issue for logging (console-only, no auto-trigger)
const queueIssueForLogging = (issue: DetectedIssue) => {
  if (typeof window === 'undefined' || !isErrorLogEnabled()) return;
  
  // Avoid duplicates in pending queue
  const exists = pendingIssues.some(
    i => i.namespace === issue.namespace && i.key === issue.key && i.language === issue.language
  );
  if (exists) return;
  
  pendingIssues.push(issue);
  
  // Log to console only when debug mode is enabled
  if (localStorage.getItem('i18n_debug') === 'true') {
    console.info('[i18n] Issue detected:', issue);
  }
};

// ==============================================
// Bootstrap Configuration (Database is Source of Truth)
// ==============================================
// 
// CRITICAL: Locale arrays MUST come from database (system_locale_configs)
// This file uses BOOTSTRAP_I18N_CONFIG for minimal i18next initialization.
// Full locale list is loaded dynamically via useI18nInitializer hook.
// 
// ESLint rule `no-hardcoded-locale-arrays` enforces:
// - No hardcoded arrays of 5+ locales
// - Ultimate fallback must be 'en', not 'en-US'
//

const NAMESPACES = [...LOCALIZATION_NAMESPACES];

// Use bootstrap config from localeConfig.ts (minimal: ['en'] with 'en' fallback)
const { supportedLngs: BOOTSTRAP_SUPPORTED_LNGS, fallbackLng: BOOTSTRAP_FALLBACK_LNG } = BOOTSTRAP_I18N_CONFIG;

type I18nResource = Record<string, unknown>;

function getInternationalLabelStrings(lng: string): { international: string; internationalOnly: string } {
  // Exact locale overrides
  if (lng === 'zh-CN') return { international: '国际', internationalOnly: '仅限国际' };
  if (lng === 'zh-HK' || lng === 'zh-TW') return { international: '國際', internationalOnly: '僅限國際' };

  const base = lng.split('-')[0];

  switch (base) {
    case 'ar':
      return { international: 'دولي', internationalOnly: 'دولي فقط' };
    case 'cs':
      return { international: 'Mezinárodní', internationalOnly: 'Pouze mezinárodní' };
    case 'de':
      return { international: 'International', internationalOnly: 'Nur international' };
    case 'en':
      return { international: 'International', internationalOnly: 'International only' };
    case 'es':
      return { international: 'Internacional', internationalOnly: 'Solo internacional' };
    case 'fr':
      return { international: 'International', internationalOnly: 'International uniquement' };
    case 'he':
      return { international: 'בינלאומי', internationalOnly: 'בינלאומי בלבד' };
    case 'hi':
      return { international: 'अंतर्राष्ट्रीय', internationalOnly: 'केवल अंतर्राष्ट्रीय' };
    case 'id':
      return { international: 'Internasional', internationalOnly: 'Hanya internasional' };
    case 'it':
      return { international: 'Internazionale', internationalOnly: 'Solo internazionale' };
    case 'ja':
      return { international: '国際', internationalOnly: '国際のみ' };
    case 'ko':
      return { international: '국제', internationalOnly: '국제만' };
    case 'nl':
      return { international: 'Internationaal', internationalOnly: 'Alleen internationaal' };
    case 'pt':
      return { international: 'Internacional', internationalOnly: 'Somente internacional' };
    case 'ru':
      return { international: 'Международный', internationalOnly: 'Только международный' };
    case 'tr':
      return { international: 'Uluslararası', internationalOnly: 'Sadece uluslararası' };
    case 'zh':
      // Fallback for any other zh-* we might add later
      return { international: '國際', internationalOnly: '僅限國際' };
    default:
      return { international: 'International', internationalOnly: 'International only' };
  }
}

function ensureInternationalLabels(ns: string, lng: string, data: I18nResource): void {
  if (ns !== 'common') return;

  const labelsRaw = data.labels;
  if (!labelsRaw || typeof labelsRaw !== 'object' || Array.isArray(labelsRaw)) return;

  const labels = labelsRaw as Record<string, unknown>;
  const { international, internationalOnly } = getInternationalLabelStrings(lng);

  if (typeof labels.international !== 'string') labels.international = international;
  if (typeof labels.international_only !== 'string') labels.international_only = internationalOnly;
}

// Custom language detector: returns 'pt-BR' on tributario.com.br domain
// Brazilian Portuguese is the primary locale for tributario.com.br users.
// Future: tributario.pt → 'pt-PT' for European Portuguese
const domainDetector = {
  name: 'domainDetector',
  lookup(): string | undefined {
    if (DOMAIN_DEFAULTS.isTributario) return 'pt-BR';
    return undefined;
  },
  cacheUserLanguage(): void {
    // No-op: domain detection doesn't cache
  },
};

// Register custom detector
const languageDetector = new LanguageDetector();
languageDetector.addDetector(domainDetector);

// Regional UI (non-email) locale files that actually exist in public/locales.
const REGIONAL_UI_LOCALES = new Set<string>(['zh-TW']);

// Initialize i18next with HTTP backend loading from static JSON files
i18n
  .use(HttpBackend)
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    // Bootstrap with permissive config - full locale list loaded from database at runtime
    // CRITICAL: Do NOT restrict supportedLngs before DB loads - causes language changes to fail
    supportedLngs: false, // PERMISSIVE: Accept any language until DB config loads
    nonExplicitSupportedLngs: true, // Accept languages not explicitly listed
    fallbackLng: BOOTSTRAP_FALLBACK_LNG, // { default: ['en'] } - 'en' is ultimate fallback
    lng: undefined, // Let language detector decide initially
    defaultNS: 'common',
    ns: NAMESPACES,
    partialBundledLanguages: true,
    detection: {
      order: ['domainDetector', 'localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'user-locale', // CRITICAL: Same key as app uses
    },
    interpolation: {
      escapeValue: false,
    },
    // keySeparator and nsSeparator use defaults ('.' and ':').
    // Translation bundles use nested object structure (e.g. {nav: {experts: "..."}})
    // so the default dot-separator is required for traversal.
    returnNull: false,
    returnEmptyString: false,
    react: {
      useSuspense: false,
      bindI18n: 'languageChanged loaded', // Re-render on load completion
      bindI18nStore: 'added removed', // Re-render on resource changes
    },
    // Load all available languages (enables switching without extra requests)
    load: 'all',
    backend: {
      // CRITICAL: Use base URL for correct asset paths (handles non-root deployments)
      loadPath: `${import.meta.env.BASE_URL || '/'}locales/{{ns}}.{{lng}}.json`,
      // Custom request with error handling
      request: async (
        options: HttpBackendOptions, 
        url: string, 
        payload: unknown, 
        callback: (err: Error | null, result: { status: number; data: Record<string, unknown> }) => void
      ) => {
        // Extract namespace and language from URL
        const match = url.match(/\/locales\/([^.]+)\.([^.]+)\.json/);
        const ns = match?.[1] ?? '';
        const lng = match?.[2] ?? '';

        // SIMPLIFIED: Always load from JSON files
        // DB mode is now only used in LocalizationEditContext for inline editing,
        // not as a primary translation source for i18next

        // Load from static JSON files with fallback chain
        const tryFetch = async (fetchUrl: string): Promise<{ ok: boolean; data: I18nResource }> => {
          try {
            const response = await fetch(fetchUrl, { method: 'GET' });
            if (response.ok) {
              return { ok: true, data: await response.json() };
            }
            return { ok: false, data: {} };
          } catch {
            return { ok: false, data: {} };
          }
        };

        // Build fallback chain. UI strings ship as neutral primaries only
        // (e.g. `pt`, `en`); regional files exist solely for `emails` and for
        // zh-TW (a distinct script). Requesting any other regional file is a
        // guaranteed 404 logged on every page load, so skip it.
        // Also strip POSIX modifiers like `en-US@posix` from the tag.
        const cleanLng = lng.split('@')[0];
        const baseLang = cleanLng.split('-')[0];
        const baseUrl = import.meta.env.BASE_URL || '/';
        const regionalExists =
          cleanLng !== baseLang && (ns === 'emails' || REGIONAL_UI_LOCALES.has(cleanLng));
        const regionalUrl = regionalExists ? `${baseUrl}locales/${ns}.${cleanLng}.json` : null;
        const baseFileUrl = `${baseUrl}locales/${ns}.${baseLang}.json`;
        const fallbackChain = [
          regionalUrl,
          baseFileUrl,
          baseLang !== 'en' ? `${baseUrl}locales/${ns}.en.json` : null, // English fallback
        ].filter(Boolean) as string[];

        let result: { ok: boolean; data: I18nResource } = { ok: false, data: {} };
        let usedUrl = url;

        for (const tryUrl of fallbackChain) {
          result = await tryFetch(tryUrl);
          if (result.ok) {
            usedUrl = tryUrl;
            break;
          }
        }

        if (!result.ok) {
          if (match) {
            queueIssueForLogging({
              namespace: ns,
              key: '_file_load',
              type: 'load_failed',
              route: typeof window !== 'undefined' ? window.location.pathname : '',
              language: lng,
            });
          }
          logger.warn(`[i18n] Failed to load ${url} and all fallbacks`);
          // Signal error to i18next so it knows loading failed
          callback(new Error(`Failed to load ${url}`), { status: 404, data: {} });
          return;
        }

        if (usedUrl !== url && typeof window !== 'undefined' && localStorage.getItem('i18n_debug') === 'true') {
          console.info(`[i18n] Used fallback: ${usedUrl} instead of ${url}`);
        }

        if (match) {
          ensureInternationalLabels(ns, lng, result.data);
        }

        callback(null, { status: 200, data: result.data });
      },
    } as HttpBackendOptions,
    // Log missing keys to database
    saveMissing: true,
    missingKeyHandler: (lngs: readonly string[], ns: string, key: string) => {
      if (typeof window === 'undefined') return;
      const lng = lngs[0] || 'unknown';
      queueIssueForLogging({
        namespace: ns,
        key,
        type: 'missing',
        route: window.location.pathname,
        language: lng,
      });
    },
    // Detect when key returns object instead of string (parent-child conflict)
    parseMissingKeyHandler: (key: string, res: unknown) => {
      if (typeof window === 'undefined') return;
      if (typeof res === 'object' && res !== null && !Array.isArray(res)) {
        // Key resolved to an object - this indicates a parent-child conflict
        const parts = key.split(':');
        const ns = parts.length > 1 ? parts[0] : 'common';
        const actualKey = parts.length > 1 ? parts[1] : key;
        queueIssueForLogging({
          namespace: ns,
          key: actualKey,
          type: 'object_returned',
          route: window.location.pathname,
          language: i18n.language || 'en-US',
        });
      }
    },
  });

// Set initial HTML direction based on detected language
// Uses runtime RTL checker (from database is_rtl column) if available,
// falls back to bootstrap checker (base language extraction)
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
  // Use runtime checker if available (loaded from DB), else bootstrap
  const rtlChecker = getRuntimeRtlChecker() || isRtlLocale;
  document.documentElement.dir = rtlChecker(lng) ? 'rtl' : 'ltr';
  
  // DEV: Log language changes for debugging
  if (typeof window !== 'undefined' && localStorage.getItem('i18n_debug') === 'true') {
    console.info('[i18n] Language changed:', {
      language: lng,
      resolved: i18n.resolvedLanguage,
      hasResources: i18n.hasResourceBundle(lng, 'common'),
    });
  }
});

// DEV: Log loading events for debugging
if (typeof window !== 'undefined' && localStorage.getItem('i18n_debug') === 'true') {
  i18n.on('loaded', (loaded) => {
    console.info('[i18n] Resources loaded:', loaded);
  });
  i18n.on('failedLoading', (lng, ns, msg) => {
    console.warn('[i18n] Failed loading:', { lng, ns, msg });
  });
}

export default i18n;
